// Accounts API service
import { baseClient } from './baseClient';
import type {
  Account,
  AccountCreate,
  AccountReadWithDetails
} from '../types/api';

export class AccountsApi {
  private static instance: AccountsApi;

  private constructor() { }

  public static getInstance(): AccountsApi {
    if (!AccountsApi.instance) {
      AccountsApi.instance = new AccountsApi();
    }
    return AccountsApi.instance;
  }

  /**
   * Get all accounts with optional pagination
   */
  async getAccounts(skip?: number, limit?: number): Promise<Account[]> {
    const params = new URLSearchParams();
    if (skip) params.append('skip', skip.toString());
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = queryString ? `/accounts?${queryString}` : '/accounts';

    return baseClient.get<Account[]>(url);
  }

  /**
   * Get a single account by ID with details (contacts and deals)
   */
  async getAccount(id: string): Promise<AccountReadWithDetails> {
    return baseClient.get<AccountReadWithDetails>(`/accounts/${id}`);
  }

  /**
   * Create a new account
   */
  async createAccount(account: AccountCreate): Promise<Account> {
    return baseClient.post<Account>('/accounts', account);
  }

  /**
   * Update an existing account
   */
  async updateAccount(id: string, account: AccountCreate): Promise<Account> {
    return baseClient.put<Account>(`/accounts/${id}`, account);
  }

  /**
   * Delete an account
   */
  async deleteAccount(id: string): Promise<void> {
    return baseClient.delete<void>(`/accounts/${id}`);
  }

  /**
   * Search accounts by name or domain
   */
  async searchAccounts(query: string): Promise<Account[]> {
    const params = new URLSearchParams();
    params.append('q', query);

    return baseClient.get<Account[]>(`/accounts?${params.toString()}`);
  }

  /**
   * Get accounts by domain
   */
  async getAccountByDomain(domain: string): Promise<Account | null> {
    try {
      const accounts = await this.searchAccounts(domain);
      return accounts.find(account => account.domain === domain) || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if account domain exists
   */
  async checkDomainExists(domain: string): Promise<boolean> {
    const account = await this.getAccountByDomain(domain);
    return account !== null;
  }

  /**
   * Get account statistics
   */
  async getAccountStats(accountId: string): Promise<any> {
    const account = await this.getAccount(accountId);

    return {
      totalContacts: account.contacts.length,
      totalDeals: account.deals.length,
      activeDeals: account.deals.filter(deal =>
        deal.stage !== 'Closed Won' && deal.stage !== 'Closed Lost'
      ).length,
      totalDealValue: account.deals.reduce((sum, deal) => sum + deal.amount, 0),
      wonDeals: account.deals.filter(deal => deal.stage === 'Closed Won').length,
      lostDeals: account.deals.filter(deal => deal.stage === 'Closed Lost').length,
    };
  }
}

// Export singleton instance
export const accountsApi = AccountsApi.getInstance(); 